import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Legislation } from "@/models/legislation/Legislation";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";
import { ConsolidationChangeStatus } from "@/models/legislation/metadata/ConsolidationMetadata";
import {
  EMPTY_CONTAINER_GROUP_KEYS,
  EMPTY_CONTAINER_IDS,
  EMPTY_CONTAINER_STATUS_BY_ID,
  EMPTY_DELETED_CONTAINER_CHANGES,
} from "@/partials/document-renderer/utils/stableEmptyProps";
import {
  ConsolidationRenderResult,
  ContainerChangeMeta,
  DeletedContainerChange,
} from "@/models/consolidation";
import RestResponse, { restError } from "@/models/client/RestResponse";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import useConsolidationRenderingEnabled from "./useConsolidationRenderingEnabled";
import {
  CONSOLIDATION_NOT_FOUND_MESSAGE,
  ConsolidationRenderTarget,
  ConsolidationUrlParams,
  resolveLegislationConsolidationRenderTarget,
} from "@/lib/consolidationMetadata";
import { getRetsinformationMetadataFromLegislation } from "@/lib/consolidationLegislationUtils";
import {
  ConsolidationSnapshot,
  selectNearestEarlierSnapshot,
} from "@/lib/consolidationCarryForward";
import { isIsoDateOnly } from "@/lib/consolidationVersion";
import { PATH_KEYS } from "@/services/appServices";
import { ConsolidationServiceType } from "@/services/consolidationService";
import { useItemService } from "./useServices";
import {
  processConsolidatedHtml,
  useLegislationContent,
} from "./useLegislationContent";

export type ConsolidationRenderContext = {
  lawExternalId: string;
  changeLaw?: string;
  asOf?: string;
  view: ConsolidationRenderTarget["view"];
};

export type LegislationViewerContentOptions = {
  legislation: Legislation | null;
  documentId: string | undefined;
  datasource: string | undefined;
  urlParams: ConsolidationUrlParams;
  timelineRoot?: string | null;
};

export type LegislationViewerContentResult = {
  rawCSS: string;
  rawHTML: string;
  loading: boolean;
  error: string | null;
  isConsolidatedRender: boolean;
  isConsolidationLoading: boolean;
  isConsolidationNotFound: boolean;
  consolidationRenderContext: ConsolidationRenderContext | null;
  consolidationAsOf: string | null;
  /**
   * The date the consolidated text is actually current through — the max
   * activation date of the incorporated change laws (API `resolvedAsOf`). Unlike
   * `consolidationAsOf` this never falls back to the requested `asOf`, so it is
   * safe to surface as the "consolidated up to" (t.o.m.) coverage date. Null
   * when the render did not resolve one (e.g. plain `as_of`-only renders).
   */
  consolidationResolvedAsOf: string | null;
  changedContainerIds: ReadonlySet<string>;
  changedContainerGroupKeys: ReadonlyMap<string, string>;
  changedContainerStatusById: ReadonlyMap<string, ConsolidationChangeStatus>;
  deletedContainerChanges: readonly DeletedContainerChange[];
};

const unwrapConsolidationResponse = async (
  responsePromise: RestResponse<ConsolidationRenderResult>
): Promise<ConsolidationRenderResult> => {
  const response = await responsePromise;
  if (response.success) return response.value;
  if (response.status === 404) {
    throw restError(response, CONSOLIDATION_NOT_FOUND_MESSAGE);
  }
  throw restError(response);
};

/**
 * Live amended containers and deleted "ghost" containers share the same
 * highlight/navigation/grouping machinery, so both feed the changed-container
 * sets. Deleted containers are re-inserted into the DOM as ghosts by the
 * renderer, so their ids resolve to real elements.
 */
type GroupableChange = {
  containerId: string;
  changeLawExternalId: string;
  changeLawPosition: number;
  originKind: string;
};

const buildChangedContainerIds = (
  containerChanges: ContainerChangeMeta[] | undefined,
  deletedChanges: readonly DeletedContainerChange[] | undefined
): ReadonlySet<string> =>
  new Set(
    [...(containerChanges ?? []), ...(deletedChanges ?? [])].map(
      (change) => change.containerId
    )
  );

/**
 * Maps each changed container to a key identifying the amendment point that
 * produced it: `${changeLawExternalId}#${changeLawPosition}`. Containers created
 * by the same point (e.g. a group heading inserted together with its paragraphs,
 * or several headings removed by one repeal) share a key, letting the navigation
 * collapse them into one changed section.
 *
 * Global replaces ("Overalt i loven …") are deliberately excluded: they touch
 * many independent containers with a shared position, so grouping them would
 * merge unrelated paragraphs whenever the replacement lands on adjacent ones.
 * Containers without a key are always treated as standalone sections.
 */
const buildChangedContainerGroupKeys = (
  containerChanges: ContainerChangeMeta[] | undefined,
  deletedChanges: readonly DeletedContainerChange[] | undefined
): ReadonlyMap<string, string> =>
  new Map(
    ([...(containerChanges ?? []), ...(deletedChanges ?? [])] as GroupableChange[])
      .filter((change) => change.originKind !== "GLOBAL_REPLACE")
      .map((change) => [
        change.containerId,
        `${change.changeLawExternalId}#${change.changeLawPosition}`,
      ])
  );

const toChangeStatus = (status: string): ConsolidationChangeStatus =>
  status === "APPROVED" ? "APPROVED" : "NEED_REVIEW";

/**
 * Per-container approval status for the render. Since the viewer always renders
 * the draft (all changes), each amended/deleted container carries its own
 * `status` so the document can annotate whether that specific change is verified
 * or still an unverified draft — never hiding pending content.
 */
export const buildChangedContainerStatusById = (
  containerChanges: ContainerChangeMeta[] | undefined,
  deletedChanges: readonly DeletedContainerChange[] | undefined
): ReadonlyMap<string, ConsolidationChangeStatus> =>
  new Map(
    [...(containerChanges ?? []), ...(deletedChanges ?? [])].map((change) => [
      change.containerId,
      toChangeStatus(change.status),
    ])
  );

const fetchConsolidationRender = (
  renderTarget: ConsolidationRenderTarget,
  consolidationService: ConsolidationServiceType,
  signal: AbortSignal
): Promise<ConsolidationRenderResult> => {
  if (renderTarget.changeLaw) {
    // A date-only `as_of` alongside `change_law` is a version pinned by the
    // dropdown and must drive the render date. Timeline diamond links instead
    // carry `approvedAt` timestamps in `as_of` (link-pinning metadata only) —
    // those must keep rendering at the change law's max activation date.
    const pinnedAsOf = isIsoDateOnly(renderTarget.asOf)
      ? renderTarget.asOf
      : undefined;
    return unwrapConsolidationResponse(
      consolidationService.renderByChangeLaw(
        {
          lawExternalId: renderTarget.lawExternalId,
          changeLaw: renderTarget.changeLaw,
          ...(pinnedAsOf ? { asOf: pinnedAsOf } : {}),
          view: renderTarget.view,
        },
        signal
      )
    );
  }

  return unwrapConsolidationResponse(
    consolidationService.render(
      {
        lawExternalId: renderTarget.lawExternalId,
        asOf: renderTarget.asOf!,
        view: renderTarget.view,
      },
      signal
    )
  );
};

/**
 * Unified content pipeline for the legislation HTML viewer.
 * Uses Consolidation API when metadata or URL params request it; otherwise silver HTML.
 */
export const useLegislationViewerContent = ({
  legislation,
  documentId,
  datasource,
  urlParams,
  timelineRoot,
}: LegislationViewerContentOptions): LegislationViewerContentResult => {
  const silverContent = useLegislationContent({ legislation, datasource });
  const { enabled: consolidationFeatureEnabled } =
    useConsolidationRenderingEnabled();
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  const isRetsinformation =
    normalizeDataSource(datasource) === DataSource.Retsinformation;

  const retsinformationMeta = useMemo((): RetsinformationMetadata | null => {
    if (!legislation) return null;
    return getRetsinformationMetadataFromLegislation(legislation);
  }, [legislation]);

  const renderTarget = useMemo(() => {
    if (!isRetsinformation) return null;

    return resolveLegislationConsolidationRenderTarget({
      documentId,
      urlParams,
      meta: retsinformationMeta,
      consolidationFeatureEnabled,
      timelineRoot,
    });
  }, [
    documentId,
    consolidationFeatureEnabled,
    isRetsinformation,
    retsinformationMeta,
    timelineRoot,
    urlParams,
  ]);

  // Timeline position per changelaw, used to order carry-forward snapshots so a
  // NOT_FOUND earlier node never borrows text from a later node (LAR-296).
  const timelineIndexByChangeLaw = useMemo(() => {
    const map = new Map<string, number>();
    for (const inbound of retsinformationMeta?.inboundChanges ?? []) {
      if (inbound.changelawAccession) {
        map.set(inbound.changelawAccession, inbound.timelineIndex);
      }
    }
    return map;
  }, [retsinformationMeta]);

  const consolidationQuery = useQuery<ConsolidationRenderResult>({
    // The viewer always renders draft, so `view` is constant and omitted from
    // the cache key.
    queryKey: [
      PATH_KEYS.consolidation,
      "viewer-render",
      renderTarget?.lawExternalId,
      renderTarget?.changeLaw,
      renderTarget?.asOf,
    ],
    queryFn: ({ signal }) =>
      fetchConsolidationRender(renderTarget!, consolidationService, signal),
    enabled: Boolean(renderTarget),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData, previousQuery) => {
      if (!previousData || !previousQuery) return undefined;
      const previousLawId = previousQuery.queryKey[2];
      const currentLawId = renderTarget?.lawExternalId;
      if (
        typeof previousLawId === "string" &&
        typeof currentLawId === "string" &&
        previousLawId === currentLawId
      ) {
        return previousData;
      }
      return undefined;
    },
    retry: (failureCount, error) => {
      if (
        error instanceof Error &&
        (error.name === "AbortError" ||
          error.message === CONSOLIDATION_NOT_FOUND_MESSAGE)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const isConsolidatedRender = Boolean(renderTarget);
  const isConsolidationNotFound =
    consolidationQuery.isFetched &&
    consolidationQuery.error instanceof Error &&
    consolidationQuery.error.message === CONSOLIDATION_NOT_FOUND_MESSAGE;

  // Process consolidation HTML only when the underlying HTML string changes —
  // never on unrelated re-renders (scroll, hover, zoom, loading toggles).
  const currentProcessedHtml = useMemo(
    () => processConsolidatedHtml(consolidationQuery.data?.html ?? "", datasource),
    [consolidationQuery.data?.html, datasource]
  );

  // Successful renders cached per anchor, keyed by the changelaw's timeline
  // index so carry-forward can pick the nearest at-or-before snapshot rather
  // than whichever render happened to succeed last (LAR-296).
  const lastSuccessfulByAnchorRef = useRef(
    new Map<string, Map<number, ConsolidationSnapshot>>()
  );

  useEffect(() => {
    const anchorLawId = renderTarget?.lawExternalId;
    const changeLaw = renderTarget?.changeLaw;
    if (
      !anchorLawId ||
      !changeLaw ||
      !consolidationQuery.data?.html?.trim() ||
      !currentProcessedHtml.trim()
    ) {
      return;
    }
    const timelineIndex = timelineIndexByChangeLaw.get(changeLaw);
    if (timelineIndex === undefined) return;

    let snapshotsByTimelineIndex =
      lastSuccessfulByAnchorRef.current.get(anchorLawId);
    if (!snapshotsByTimelineIndex) {
      snapshotsByTimelineIndex = new Map<number, ConsolidationSnapshot>();
      lastSuccessfulByAnchorRef.current.set(anchorLawId, snapshotsByTimelineIndex);
    }
    snapshotsByTimelineIndex.set(timelineIndex, {
      processedHtml: currentProcessedHtml,
      result: consolidationQuery.data,
    });
  }, [
    consolidationQuery.data,
    currentProcessedHtml,
    renderTarget?.lawExternalId,
    renderTarget?.changeLaw,
    timelineIndexByChangeLaw,
  ]);

  const carriedForwardConsolidation =
    isConsolidationNotFound && renderTarget?.changeLaw
      ? selectNearestEarlierSnapshot(
          lastSuccessfulByAnchorRef.current.get(renderTarget.lawExternalId),
          timelineIndexByChangeLaw.get(renderTarget.changeLaw)
        )
      : undefined;

  const effectiveConsolidationNotFound =
    isConsolidationNotFound && !carriedForwardConsolidation;

  const consolidationFetchSettled =
    consolidationQuery.isFetched || effectiveConsolidationNotFound;

  const consolidatedHtml = currentProcessedHtml.trim()
    ? currentProcessedHtml
    : (carriedForwardConsolidation?.processedHtml ?? "");

  const consolidationError =
    consolidationQuery.error instanceof Error
      ? consolidationQuery.error.message
      : consolidationQuery.error
        ? "Kunne ikke hente konsolideret tekst"
        : null;

  // A NOT_FOUND with a carry-forward snapshot is an expected state, not a
  // failure: keep the carried-forward consolidated HTML instead of falling back
  // to silver. Only genuine (non-NOT_FOUND) fetch errors force silver here; the
  // no-snapshot NOT_FOUND case is already covered by effectiveConsolidationNotFound.
  const hasBlockingConsolidationError =
    Boolean(consolidationError) && !isConsolidationNotFound;

  const shouldFallbackToSilver =
    effectiveConsolidationNotFound ||
    (consolidationFetchSettled &&
      (!consolidatedHtml.trim() || hasBlockingConsolidationError));

  const displayHtml = shouldFallbackToSilver
    ? silverContent.rawHTML
    : consolidatedHtml;

  const consolidationRenderContext = useMemo((): ConsolidationRenderContext | null => {
    if (!renderTarget) return null;
    return {
      lawExternalId: renderTarget.lawExternalId,
      changeLaw: renderTarget.changeLaw,
      asOf: renderTarget.asOf,
      view: renderTarget.view,
    };
  }, [renderTarget]);

  const isActiveConsolidatedRender =
    isConsolidatedRender &&
    (!consolidationFetchSettled || !shouldFallbackToSilver);

  const consolidationAsOf = useMemo((): string | null => {
    if (!isActiveConsolidatedRender) return null;
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    if (!source?.html?.trim()) return null;
    const resolved = source.resolvedAsOf ?? source.asOf;
    return resolved ?? renderTarget?.asOf ?? null;
  }, [
    carriedForwardConsolidation,
    consolidationQuery.data,
    isActiveConsolidatedRender,
    renderTarget?.asOf,
  ]);

  const consolidationResolvedAsOf = useMemo((): string | null => {
    if (!isActiveConsolidatedRender) return null;
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    if (!source?.html?.trim()) return null;
    return source.resolvedAsOf ?? null;
  }, [
    carriedForwardConsolidation,
    consolidationQuery.data,
    isActiveConsolidatedRender,
  ]);

  const deletedContainerChanges = useMemo(() => {
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    return source?.deletedContainerChanges ?? [];
  }, [carriedForwardConsolidation, consolidationQuery.data]);

  const changedContainerIds = useMemo(() => {
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    return buildChangedContainerIds(
      source?.containerChanges,
      source?.deletedContainerChanges
    );
  }, [carriedForwardConsolidation, consolidationQuery.data]);

  const changedContainerGroupKeys = useMemo(() => {
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    return buildChangedContainerGroupKeys(
      source?.containerChanges,
      source?.deletedContainerChanges
    );
  }, [carriedForwardConsolidation, consolidationQuery.data]);

  const changedContainerStatusById = useMemo(() => {
    const source =
      consolidationQuery.data ?? carriedForwardConsolidation?.result;
    return buildChangedContainerStatusById(
      source?.containerChanges,
      source?.deletedContainerChanges
    );
  }, [carriedForwardConsolidation, consolidationQuery.data]);

  const consolidationLoading =
    Boolean(renderTarget) &&
    consolidationQuery.isFetching &&
    !consolidatedHtml.trim();

  if (!isConsolidatedRender) {
    return {
      rawCSS: silverContent.rawCSS,
      rawHTML: silverContent.rawHTML,
      loading: silverContent.loading,
      error: silverContent.error,
      isConsolidatedRender: false,
      isConsolidationLoading: false,
      isConsolidationNotFound: false,
      consolidationRenderContext: null,
      consolidationAsOf: null,
      consolidationResolvedAsOf: null,
      changedContainerIds: EMPTY_CONTAINER_IDS,
      changedContainerGroupKeys: EMPTY_CONTAINER_GROUP_KEYS,
      changedContainerStatusById: EMPTY_CONTAINER_STATUS_BY_ID,
      deletedContainerChanges: EMPTY_DELETED_CONTAINER_CHANGES,
    };
  }

  return {
    rawCSS: silverContent.rawCSS,
    rawHTML: displayHtml,
    loading: silverContent.loading || consolidationLoading,
    error: effectiveConsolidationNotFound
      ? null
      : consolidationError || silverContent.error,
    isConsolidatedRender: isActiveConsolidatedRender,
    isConsolidationLoading: consolidationLoading,
    isConsolidationNotFound: effectiveConsolidationNotFound,
    consolidationRenderContext,
    consolidationAsOf,
    consolidationResolvedAsOf,
    changedContainerIds,
    changedContainerGroupKeys,
    changedContainerStatusById,
    deletedContainerChanges,
  };
};
